import { useState, useEffect, useCallback } from 'react';
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser } from '../../../hooks/useUsers';
import { useRoles } from '../../../hooks/useRoles';
import { ITEMS_PER_PAGE, INITIAL_USER_STATE } from '../constants/usersConstants';
import { filterUsers } from '../utils/usersUtils';
import { usePaginatedFilter } from '../../../common/hooks/usePaginatedFilter';

export const useUsersTable = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(INITIAL_USER_STATE);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const { data: usersData = [], isLoading: usersLoading } = useUsers();
  const { data: roles = [] } = useRoles();

  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();
  const deleteMutation = useDeleteUser();

  const {
    data,
    totalCount,
    totalPages,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePaginatedFilter({
    data: usersData,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn: filterUsers,
  });

  const isLoading =
    usersLoading ||
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  const handleOpenDialog = useCallback(() => {
    setSelectedUser(INITIAL_USER_STATE);
    setEditMode(false);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedUser(INITIAL_USER_STATE);
  }, []);

  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setSelectedUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSelectChange = useCallback((event) => {
    const { name, value } = event.target;
    setSelectedUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const sanitizeUserData = useCallback((user, { includePassword = true } = {}) => {
    if (!user) return {};

    const {
      uuid,
      uid_firebase,
      created_at,
      updated_at,
      deleted_at,
      branches,
      role,
      ...rest
    } = user;

    const payload = {
      names: rest.names || '',
      email: rest.email || '',
      telephone: rest.telephone || '',
      is_active: rest.is_active,
      is_administrator: rest.is_administrator,
      uuid_rol: rest.uuid_rol || null,
      timezone: rest.timezone || '',
      uuid_subscriber: rest.uuid_subscriber ?? null,
    };

    if (includePassword && rest.password) {
      payload.password = rest.password;
    }

    return payload;
  }, []);

  const handleSave = useCallback(async () => {
    try {
      if (editMode) {
        if (!selectedUser?.uuid) {
          throw new Error('No se encontró el identificador del usuario a editar');
        }

        const userPayload = sanitizeUserData(selectedUser, { includePassword: false });

        await updateMutation.mutateAsync({
          id: selectedUser.uuid,
          user: userPayload,
        });
      } else {
        const newUserPayload = sanitizeUserData(selectedUser, { includePassword: true });
        await createMutation.mutateAsync(newUserPayload);
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving user:', error);
    }
  }, [
    editMode,
    selectedUser,
    updateMutation,
    createMutation,
    handleCloseDialog,
    sanitizeUserData,
  ]);

  const handleEdit = useCallback((user) => {
    setSelectedUser(user);
    setEditMode(true);
    setDialogOpen(true);
  }, []);

  const handleDelete = useCallback(
    (uuid) => {
      const user = usersData.find((u) => u.uuid === uuid);
      setUserToDelete(user);
      setDeleteConfirmOpen(true);
    },
    [usersData]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (userToDelete) {
      try {
        await deleteMutation.mutateAsync(userToDelete.uuid);
        setDeleteConfirmOpen(false);
        setUserToDelete(null);
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  }, [userToDelete, deleteMutation]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setUserToDelete(null);
  }, []);

  useEffect(() => {
    if (!editMode && dialogOpen && roles.length > 0) {
      setSelectedUser((prev) => ({
        ...prev,
        is_active: true,
        is_administrator: false,
        uuid_rol: roles[0]?.uuid || '',
      }));
    }
  }, [editMode, dialogOpen, roles]);

  return {
    data,
    isLoading,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    searchTerm,
    dialogOpen,
    editMode,
    selectedUser,
    roles,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleInputChange,
    handleSelectChange,
    handleSave,
    handleEdit,
    handleDelete,
    deleteConfirmOpen,
    userToDelete,
    handleConfirmDelete,
    handleCancelDelete,
  };
};
